import type { ComponentType } from 'react'
import {
  MANIFEST,
  NOT_FOUND,
  flattenRoutes,
  type FlatRoute,
  type NavSection,
  type RouteNode,
} from '../nav/manifest.ts'
import { loaderFor, type ScreenLoader } from './screenRegistry.ts'

/**
 * The route table, generated from the manifest.
 *
 * Nothing here enumerates a screen. If you can find a route name written down in this file,
 * the object model pattern has been broken (CONSOLES.md §5: one descriptor per object,
 * generating its surfaces - adding an object is a descriptor, never four screens).
 *
 * This module carries no JSX and imports no router, for one practical reason: it is the
 * thing the lane's exit condition is about, so it has to be runnable. `routes.check.ts`
 * executes it under plain `node` with nothing installed. The JSX adapter is `index.tsx`, and
 * the router is chosen by the app entry point (D23 - the router is the Admin Console's
 * choice, already made, not this lane's to make again).
 */
export interface RouteTableEntry {
  /** Stable id from the manifest. Routers ignore extra keys; the check does not. */
  id: string
  /** Present unless this is the index child. */
  path?: string
  index?: true
  /**
   * The component to render, and the props it needs. `index.ts` turns this into an element.
   *
   * Present for SHELL screens — the EmptyState behind every `dayN` route, and the catch-all.
   * Both are imported statically because they are always needed and they are tiny.
   */
  Component?: ComponentType<{ route: FlatRoute }> | ComponentType
  /**
   * The screen to load, for a `live` route. Exactly one of this and `Component` is set.
   *
   * A loader rather than a component because a screen is loaded on first render of its route
   * (see `screenRegistry.ts` — the eager version put the mock fleet API in the entry chunk of
   * every page, which is wf-12 E-06). This module cannot turn it into a component itself:
   * `React.lazy` is a react RUNTIME import, and this file is executed by `routes.check.ts`
   * under plain `node` with nothing installed. `index.ts` is where react is allowed.
   */
  load?: ScreenLoader
  route: FlatRoute | null
  children?: RouteTableEntry[]
}

export interface ShellComponents {
  /** Rendered for every `dayN` route. One component, not one per route. */
  pending: ComponentType<{ route: FlatRoute }>
  /** Rendered for a path the manifest does not claim. */
  notFound: ComponentType
}

type Rendered =
  | { Component: ComponentType<{ route: FlatRoute }> | ComponentType }
  | { load: ScreenLoader }

/**
 * What a route renders: the shared EmptyState, or a screen to load.
 *
 * The throw below still happens HERE, at table-build time, and keeping it there was the
 * constraint that shaped the lazy split. `screenRegistry.ts` learns which route id each screen
 * claims eagerly — only the components are deferred — so "declared live and nothing claims it"
 * is still caught before anything renders rather than surfacing as a blank page on the first
 * navigation to that route.
 */
function renderedBy(route: FlatRoute, shell: ShellComponents): Rendered {
  if (route.status !== 'live') return { Component: shell.pending }

  const load = loaderFor(route.id)
  if (!load) {
    /*
     * A route declared live with no screen behind it is the one failure that must not be
     * quiet. Falling back to the EmptyState would be indistinguishable from work not yet
     * started, and the manifest would then be lying about its own `status` field - the single
     * property the day-one-manifest idea rests on.
     */
    throw new Error(
      `route "${route.id}" is declared live in the manifest and no screen module claims it. ` +
        'Add src/screens/<area>/<screen>/route.tsx exporting routeId, or set the status back to dayN.',
    )
  }
  return { load }
}

function toEntry(node: RouteNode, byId: Map<string, FlatRoute>, shell: ShellComponents): RouteTableEntry {
  // The full path comes from `flattenRoutes` and is never recomputed here. Two
  // implementations of "join a child onto its parent" is how a route resolves in the check
  // and not in the browser, which is the failure this file exists to make impossible.
  const route = byId.get(node.id)
  if (!route) throw new Error(`route "${node.id}" is not in the manifest it came from`)

  const entry: RouteTableEntry =
    node.path === ''
      ? { index: true, id: node.id, ...renderedBy(route, shell), route }
      : { path: node.path, id: node.id, ...renderedBy(route, shell), route }

  if (node.children?.length) {
    entry.children = node.children.map((child) => toEntry(child, byId, shell))
  }
  return entry
}

/**
 * The complete table: every route in CONSOLES.md §2, plus the catch-all last.
 *
 * Building this is the whole of L1-console-shell. Later lanes change a `status` and add a
 * screen module under `src/screens/`. Nothing later adds an entry here, and `routes.check.ts`
 * is what says so.
 */
export function buildRouteTable(
  shell: ShellComponents,
  manifest: NavSection[] = MANIFEST,
): RouteTableEntry[] {
  const byId = new Map(flattenRoutes(manifest).map((r) => [r.id, r]))
  const table: RouteTableEntry[] = []
  for (const section of manifest) {
    for (const route of section.routes) table.push(toEntry(route, byId, shell))
  }
  table.push({ path: NOT_FOUND.path, id: NOT_FOUND.id, Component: shell.notFound, route: null })
  return table
}

/** Every entry, parents and children, in table order. */
export function flattenTable(table: RouteTableEntry[]): RouteTableEntry[] {
  return table.flatMap((entry) => [entry, ...flattenTable(entry.children ?? [])])
}
